import { useMemo, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'

import { getErrorMessage } from '@/api/api-client'
import { useCreatePickupOrder } from '@/api/pickup-orders/queries/usePickupOrders'
import { AppDialog } from '@/components/AppDialog'
import { AppTooltip } from '@/components/AppTooltip'
import { CopyTextButton } from '@/components/CopyTextButton'
import { Divider } from '@/components/Divider'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useToast } from '@/hooks/useToast'
import { cn } from '@/lib/utils'

import { PkgWarehouse } from '../../../../api/user-package/types/UserPkg'
import InfoIcon from '../../../../components/icons/info.svg?react'
import { WarehousePreview } from '../../../../components/WarehousePreview'
import { UploadArea } from '../../ReceiptUpload/components/UploadArea'
import storeSelectImage from './store-select-example.png'
import { WarehouseAddressPreview } from './WarehouseAddressPreview'

interface PrimarkFormProps {
  fullName: string
}

type PrimarkFormData = {
  pickupDate: string
  orderNumber: string
  barcodeFile: File | null
}

const defaultValues: PrimarkFormData = {
  pickupDate: '',
  orderNumber: '',
  barcodeFile: null,
}

interface PrimarkHowItWorksDialogProps {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
}

function PrimarkHowItWorksDialog({ isOpen, onOpenChange }: PrimarkHowItWorksDialogProps) {
  return (
    <AppDialog
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      titleClassName='px-8'
      title='איך עובד שירות Primark UK Click & Collect של שיפטנבול?'
    >
      <div className='flex flex-col gap-4 text-right'>
        <p className='text-text-primary text-sm font-medium'>בקיצור ולעניין: אתם מזמינים בפריימרק אנגליה ואנחנו אוספים בשבילכם מהחנות :)</p>
        <h3 className='text-text-primary mt-2 text-sm font-semibold'>איך זה עובד, צעד־צעד</h3>
        <ol className='text-text-secondary list-decimal space-y-3 ps-4 text-sm'>
          <li>
            <span className='font-medium'>מזמינים באתר Primark UK</span>
            <br />
            בוחרים את המוצרים באתר הרשמי של פריימרק אנגליה.
          </li>
          <li>
            <span className='font-medium'>בוחרים Click & Collect כבר בעמוד הראשי</span>
            <br />
            בחירת Click & Collect מתבצעת בשלב הראשון באתר.
            <br />
            רק אחרי שבוחרים Click & Collect אפשר להוסיף מוצרים לסל ולהמשיך להזמנה
          </li>
          <li>
            <span className='font-medium'>בוחרים סניף לאיסוף בבריטניה</span>
            <br />
            זה הסניף שממנו אנחנו נגיע לאסוף את ההזמנה.
            <div className='bg-muted/30 border-border-default mt-2 flex aspect-video items-center justify-center rounded-lg border-2 border-dashed'>
              <img src={storeSelectImage} alt='Store Select' className='max-h-full max-w-full object-contain' />
            </div>
            <p className='text-text-muted mt-1 text-xs'>כך נראה שלב בחירת הסניף באתר Primark UK.</p>
            <p className='text-text-secondary mt-1 text-xs'>את שם הסניף שמופיע כאן, מעתיקים לשדה &quot;Pickup Store&quot;.</p>
          </li>
          <li>
            <span className='font-medium'>מקבלים מיילים מפריימרק עם ברקוד לאיסוף</span>
            <br />
            לאחר השלמת ההזמנה, פריימרק שולחים מיילים עם פרטי האיסוף. באחד המיילים מופיע ברקוד לאיסוף מהסניף.
            <br />
            את הברקוד הזה צריך לצלם או לשמור, ולהעלות כאן צילום מסך ברור שלו.
          </li>
          <li>
            <span className='font-medium'>אנחנו אוספים מהחנות</span>
            <br />
            ברגע שהכול תקין, אנחנו נכנסים לסניף ואוספים את ההזמנה בשבילכם.
          </li>
        </ol>
        <div className='border-border-default bg-muted/20 mt-2 rounded-lg border p-3'>
          <h3 className='text-text-primary mb-2 text-sm font-semibold'>מה חשוב לדעת</h3>
          <p className='text-text-secondary text-sm'>השירות עובד רק עם הזמנות Click & Collect מפריימרק אנגליה.</p>
          <p className='text-text-secondary text-sm'>זה לא משלוח רגיל ולא כתובת למשלוח.</p>
          <p className='text-text-secondary text-sm'>בלי בחירת סניף ובלי ברקוד, אי אפשר לאסוף.</p>
        </div>
        <Button onClick={() => onOpenChange(false)}>סגור, ממשיכים לאסוף</Button>
      </div>
    </AppDialog>
  )
}

export const PrimarkForm = ({ fullName }: PrimarkFormProps) => {
  const createMutation = useCreatePickupOrder()
  const { showSuccessToast, showErrorToast } = useToast()
  const [isAgreed, setIsAgreed] = useState(false)
  const [uploadAreaKey, setUploadAreaKey] = useState(0)
  const [isHowItWorksOpen, setIsHowItWorksOpen] = useState(false)
  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<PrimarkFormData>({
    defaultValues,
    mode: 'onSubmit',
  })

  const onSubmit = async (data: PrimarkFormData) => {
    if (!isAgreed) {
      showErrorToast('יש לאשר שהפרטים והברקוד נכונים')
      return
    }

    try {
      // Create the pickup order with file (upload happens on server)
      await createMutation.mutateAsync({
        pickupDate: data.pickupDate,
        orderNumber: data.orderNumber.trim(),
        barcodeFile: data.barcodeFile,
      })

      // Reset form on success
      reset(defaultValues)
      // Reset UploadArea by remounting it with a new key
      setUploadAreaKey(prev => prev + 1)
      showSuccessToast('החבילה נקלטה במערכת!', 'החבילה זמינה עכשיו בעמוד ׳החבילות שלי׳. לאחר האיסוף, הפרטים יתעדכנו ותוכלו להמשיך לשילוח.')
    } catch (error) {
      // Error is handled by the mutation's error state
      showErrorToast('שגיאה ביצירת ההזמנה. נסה שוב.', getErrorMessage(error))
    }
  }
  const tomorrow = useMemo(() => {
    const tomorrow = new Date()
    tomorrow.setDate(tomorrow.getDate() + 1)
    return tomorrow
  }, [])
  return (
    <Card className='border border-[#d0d5dd] bg-white shadow-[0px_1px_3px_0px_rgba(0,0,0,0.08),0px_1px_2px_0px_rgba(0,0,0,0.05)]'>
      <CardContent className='p-6'>
        <div className=''>
          {/* Card Header */}
          <div className='mb-2 flex items-center gap-2'>
            <WarehousePreview warehouse={PkgWarehouse.UK} />
            <h2 className='text-text-primary text-right leading-6 font-medium'>פרטי איסוף להזמנת Primark UK</h2>
          </div>
          <div className='mb-6 text-right'>
            <p className='text-text-secondary text-sm leading-5'>
              כדי שנוכל לאסוף את ההזמנה שלכם, הזינו כאן את פרטי ה-Click & Collect מפריימרק.
            </p>
            <button
              type='button'
              onClick={() => setIsHowItWorksOpen(true)}
              className='text-text-link mt-1 inline-flex cursor-pointer items-center gap-1 text-sm underline hover:no-underline'
            >
              איך זה עובד?
            </button>
          </div>
          <PrimarkHowItWorksDialog isOpen={isHowItWorksOpen} onOpenChange={setIsHowItWorksOpen} />
          <form onSubmit={handleSubmit(onSubmit)}>
            <WarehouseAddressPreview
              index={0}
              isAddressLine2={false}
              field={{ label: 'Full Name', value: fullName, helperText: 'השם המלא שמופיע בהזמנה בפריימארק' }}
            />
            <Divider className='bg-border-default mt-4 mb-3' />
            <div className={cn('flex items-center justify-between')}>
              <div className='flex items-center gap-3'>
                <CopyTextButton value='Staines-upon-Thames' tooltipText='הועתק!' />
              </div>
              <div className='flex flex-col items-end text-end'>
                <p className='text-text-secondary mb-1 text-sm leading-5'>Pickup Store</p>
                <p dir='ltr' className='text-text-primary text-left text-sm leading-5 font-medium whitespace-pre-line'>
                  Staines
                </p>
                <p className='text-text-primary text-xs leading-4'>Staines-upon-Thames, Elmsleigh Shopping Centre, 62 South Street</p>
                <p className='text-text-secondary mt-2 text-xs leading-4'>הסניף שבחרתם בפריימארק לאיסוף ההזמנה </p>
              </div>
            </div>
            <Divider className='bg-border-default mt-4 mb-3' />
            {/* Pickup Date Field */}
            <Input
              {...register('pickupDate', { required: 'יש להזין תאריך איסוף' })}
              labelProps={{ dir: 'ltr', className: 'mb-4' }}
              label='Pickup Date'
              id='pickup-date'
              type='date'
              min={tomorrow.toISOString().split('T')[0]}
              className='h-12 w-full rounded-2xl border-[#e5e9f2]'
              placeholder='MM/DD/YYYY'
              aria-invalid={!!errors.pickupDate}
              helperText={errors.pickupDate?.message}
              error={!!errors.pickupDate}
            />
            <p className='text-text-secondary mt-2 text-left text-xs leading-4'>התאריך שבחרתם בפריימארק לאיסוף ההזמנה</p>
            <Divider className='bg-border-default mt-4 mb-3' />

            <Input
              {...register('orderNumber', { required: 'יש להזין מספר הזמנה' })}
              labelProps={{ dir: 'ltr', className: 'mb-4' }}
              label='Order Number'
              id='order-number'
              type='text'
              className='h-12 w-full rounded-2xl border-[#e5e9f2]'
              placeholder='GB1234567890'
              dir='ltr'
              aria-invalid={!!errors.orderNumber}
              helperText={errors.orderNumber?.message}
              error={!!errors.orderNumber}
            />
            <p className='text-text-secondary mt-2 text-left text-xs leading-4'>מספר ההזמנה שקיבלתם מפריימארק אחרי ביצוע ההזמנה</p>
            <Divider className='bg-border-default mt-4 mb-3' />

            {/* Upload Barcode Field */}
            <div className='pb-4'>
              <div className='mb-4 flex w-full flex-col items-end'>
                <Label htmlFor='barcode-upload' className='text-text-primary text-left text-sm font-normal'>
                  Upload Barcode
                </Label>
                <p className='text-text-secondary mt-2 text-left text-xs leading-4'>העלו כאן את הברקוד שקיבלתם במייל או באסמס מפריימארק</p>
              </div>
              <Controller
                name='barcodeFile'
                control={control}
                rules={{ required: 'יש להעלות את הברקוד' }}
                render={({ field }) => (
                  <UploadArea
                    helperImageUrl={
                      'https://shippingappturkeyb5a0792ba4e74d46b808a98e88c944140118-dev.s3.us-east-1.amazonaws.com/primark-barcode-example.jpeg'
                    }
                    maxFileCount={1}
                    key={uploadAreaKey}
                    onChangeFile={file => {
                      field.onChange(file)
                    }}
                  />
                )}
              />
              {errors.barcodeFile && <div className='text-destructive mt-2 text-right text-sm'>{errors.barcodeFile.message}</div>}
              <AppTooltip content='הברקוד הוא מסמך שמאשר שקיבלת את ההזמנה מהאימייל או הטלפון שלך'>
                <div className='text-text-link mt-2 flex items-center gap-2 text-xs underline'>
                  <InfoIcon className='size-4' />
                  מהו ברקוד לאיסוף ואיך הוא נראה?
                </div>
              </AppTooltip>
            </div>
            <Checkbox
              label='אני מאשר.ת שכל הפרטים והברקוד נכונים ותואמים להזמנת Primark UK שלי'
              labelClassName='text-text-primary text-sm font-normal'
              checked={isAgreed}
              onCheckedChange={checked => setIsAgreed(checked === 'indeterminate' ? false : checked)}
            />

            {/* Submit Button */}
            <div className='flex flex-col items-center gap-2 pt-4'>
              <Button
                type='submit'
                loading={createMutation.isPending}
                disabled={createMutation.isPending || !isAgreed}
                className='h-12 w-[335px] rounded-full bg-[#fad600] font-medium text-[#333] hover:bg-[#fad600]/90'
              >
                יאללה, תאספו לי את הפריימרק
              </Button>
              <small className='text-text-secondary max-w-[335px] text-center text-xs'>
                זהו שירות <span className='font-bold'>איסוף בלבד</span> מפריימרק אנגליה. אין כאן כתובת משלוח, ואין להשתמש בו להזמנות מאתרים
                אחרים.
              </small>
            </div>
          </form>
        </div>
      </CardContent>
    </Card>
  )
}
